import React, { FC, Fragment, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@material-ui/core';
import colors from '../../themes/main-colors';
import useContentMgmt, {
  PageTextContentType,
  ProductData,
} from '../../hooks/useContentMgmt';
import JobSummarySection, { JobSummarySectionProps } from './JobSummarySection';
import {
  ApplicationEvSectionType,
  HomeType,
  BreakerAmperageRating,
  PanelDistance,
  PanelLocation,
  PanelSideLocation,
  RebateSmartCharging,
  SectionEvAccountInfoData,
  SectionEvChargerSelectionData,
  SectionEvInstallData,
  SectionEvPanelInfoData,
  SectionEvRebatesData,
} from '../../__generated__/pgeplus-types';
import { GetJobOutputEvType } from '../../hooks/pge-plus/useInstallerJobDetailsQuery';
import microcopyGroupIds from './microcopyGroupIds';
import { titleCase } from '../../util/string-utils';
import ImageCarouselModal from './ImageCarouselModal';
import { usePrintOrDownload } from '../../hooks/usePrintOrDownload';
import { formatLocalDate } from '../../util/date-utils';
import { toCurrencyString } from '../../util/format';
import { resolveChargerDisplayName } from './utils/chargerDisplay';

interface Props {
  content: PageTextContentType;
  data: GetJobOutputEvType;
  isLinkDownload?: Boolean;
  showCustomerInfoSection?: Boolean;
}

export const JobDetailsCard: FC<Props> = ({
  content,
  data,
  isLinkDownload = false,
  showCustomerInfoSection = false,
}: Props) => {
  const [isImageViewerVisible, setIsImageViewerVisible] = useState<boolean>(
    false,
  );
  const [uploadedPhotos, setUploadedPhotos] = useState<
    { photo: string; title: string; description: string }[]
  >([]);
  const {
    loading: isDownloading,
    handleDownloadWithImage,
  } = usePrintOrDownload({
    containerId: 'print_section',
    filename: `pge_plus_job_${data.job.id}.pdf`,
  });
  const { content: contentContactInfo, getProductList } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_CONTACT_INFO,
  );
  const { content: contentInstallation } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLATION_LOCATION,
  );
  const { content: contentUploadPhotos } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_UPLOAD_PHOTOS,
  );
  const [evChargerProductList, setEvChargerProductList] = useState<
    ProductData[]
  >([]);

  const sectionEvPanelInfo = data.application.details.find(
    section => section.sectionType === ApplicationEvSectionType.EvPanelInfo,
  )?.data as SectionEvPanelInfoData;

  const sectionEvAccountInfo = data.application.details.find(
    section => section.sectionType === ApplicationEvSectionType.EvAccountInfo,
  )?.data as SectionEvAccountInfoData;

  const sectionEvChargerInfo = data.application.details.find(
    section =>
      section.sectionType === ApplicationEvSectionType.EvChargerSelection,
  )?.data as SectionEvChargerSelectionData;

  const sectionEvInstallInfo = data.application.details.find(
    section => section.sectionType === ApplicationEvSectionType.EvInstall,
  )?.data as SectionEvInstallData;

  const sectionEvRebatesInfo = data.application.details.find(
    section => section.sectionType === ApplicationEvSectionType.EvRebates,
  )?.data as SectionEvRebatesData;

  const rebateEligibility = sectionEvRebatesInfo.rebateEligibility
    ?.evRebate as RebateSmartCharging;

  useEffect(() => {
    const fetchProductList = async (productListEntryId: string) => {
      const productList = await getProductList({
        productListEntryId,
      });
      setEvChargerProductList(productList);
    };
    fetchProductList(
      sectionEvChargerInfo?.suppliedByCustomer
        ? microcopyGroupIds.PGE_PLUS_CONFIRM_YOUR_CHARGER_PRODUCT_LIST
        : microcopyGroupIds.PGE_PLUS_SELECT_CHARGER_PRODUCT_LIST,
    ).catch(err => {
      console.log('fetchProductList error: ', err);
    });
  }, [sectionEvChargerInfo]);

  useEffect(() => {
    if (sectionEvPanelInfo) {
      setUploadedPhotos(prevPhotos => [
        ...prevPhotos,
        {
          photo: sectionEvPanelInfo.panelImageUrl,
          title: content.get('PANEL_OPEN_DOOR_TITLE') || '',
          description: content.get('YOUR_PANEL_OPEN_DOOR_INFO') || '',
        },
        {
          photo: sectionEvPanelInfo.amperageImageUrl,
          title: content.get('AMPERAGE_PHOTO_TITLE') || '',
          description: content.get('AMPERAGE_MAIN_BREAKER_INFO') || '',
        },
        {
          photo: sectionEvPanelInfo.panelLocationImageUrl,
          title: content.get('PANEL_LOCATION_PHOTO_TITLE') || '',
          description: content.get('YOUR_PANEL_OPEN_DOOR_INFO') || '',
        },
      ]);

      if (sectionEvPanelInfo.installDistance?.extendedInstallDistance) {
        setUploadedPhotos(prevPhotos => [
          ...prevPhotos,
          {
            photo:
              sectionEvPanelInfo.installDistance
                ?.extendedInstallDistanceImageUrl,
            title: content.get('PANEL_INSTALLATION_DISTANCE_TITLE') || '',
            description: content.get('YOUR_PANEL_LOCATION_INFO') || '',
          },
        ]);
      }
    }
  }, [sectionEvPanelInfo]);

  const onViewAll = () => {
    setIsImageViewerVisible(true);
  };

  const HOME_TYPES = {
    [HomeType.Detached]: contentContactInfo.get('DETACHED'),
    [HomeType.Townhouse]: contentContactInfo.get('TOWNHOUSE'),
    [HomeType.Duplex]: contentContactInfo.get('DUPLEX'),
    [HomeType.Triplex]: contentContactInfo.get('TRIPLEX'),
    [HomeType.Apartment]: contentContactInfo.get('APARTMENT'),
    [HomeType.Condo]: contentContactInfo.get('CONDO'),
  };


  const PANEL_LOCATION = {
    [PanelLocation.GarageAttached]: contentInstallation.get('ATTACHED_GARAGE'),
    [PanelLocation.Basement]: contentInstallation.get('BASEMENT'),
    [PanelLocation.Inside]: contentInstallation.get('INSIDE_THE_HOME'),
    [PanelLocation.Outside]: contentInstallation.get('OUTSIDE_THE_HOME'),
    [PanelLocation.NotSure]: contentInstallation.get('NOT_SURE'),
    [PanelLocation.Other]: contentInstallation.get('OTHER'),
  };

  const DISTANCE_TO_PANEL = {
    [PanelDistance.Lt_5]: contentInstallation.get('5_FEET_OR_LESS'),
    [PanelDistance.Btw_6_30]: contentInstallation.get('6_TO_30_FEET'),
    [PanelDistance.Gt_30]: contentInstallation.get('MORE_THAN_30_FEET'),
  };

  const PANEL_SIDE_LOCATION = {
    [PanelSideLocation.Left]: contentInstallation.get('LEFT_SIDE'),
    [PanelSideLocation.BackWall]: contentInstallation.get('BACK_SIDE'),
    [PanelSideLocation.Right]: contentInstallation.get('RIGHT_SIDE'),
  };

  const MAIN_BREAKER_AMPS = {
    [BreakerAmperageRating.Unknown]: contentUploadPhotos.get('IM_NOT_SURE'),
    [BreakerAmperageRating.Amps_320]: contentUploadPhotos.get('320_AMPS'),
    [BreakerAmperageRating.Amps_200]: contentUploadPhotos.get('200_AMPS'),
    [BreakerAmperageRating.Amps_150]: contentUploadPhotos.get('150_AMPS'),
    [BreakerAmperageRating.Amps_125]: contentUploadPhotos.get('125_AMPS'),
    [BreakerAmperageRating.Amps_100]: contentUploadPhotos.get('100_AMPS'),
    [BreakerAmperageRating.NoMainBreaker]: contentUploadPhotos.get(
      'NO_MAIN_BREAKER',
    ),
  };

  const chargerDisplayName = resolveChargerDisplayName(
    {
      productId: sectionEvChargerInfo?.productId,
      suppliedByCustomer: sectionEvChargerInfo?.suppliedByCustomer,
      validatedProductName:
        sectionEvChargerInfo?.chargerValidation?.product?.name,
    },
    evChargerProductList,
  );

  const summaryData = [
    {
      sectionTitle: content.get('CUSTOMER_INFORMATION'),
      items: [
        {
          label: content.get('CUSTOMER_IS_HOMEOWNER'),
          values: [sectionEvAccountInfo?.isOwner ? 'Yes' : 'No'],
        },
        {
          label: content.get('TYPE_OF_HOME'),
          values: [HOME_TYPES[sectionEvAccountInfo?.homeType!] || 'NA'],
        },
      ],
    },
    {
      sectionTitle: content.get('PRODUCT'),
      items: [
        {
          label: content.get('CHARGER_SELECTION'),
          values: [chargerDisplayName],
        },
      ],
    },
    {
      sectionTitle: content.get('REBATE_ELIGIBILITY'),
      items: [
        {
          label: content.get('ESTIMATED_REBATES'),
          values: [
            rebateEligibility?.charger
              ? toCurrencyString(rebateEligibility.charger, true)
              : rebateEligibility?.amount
              ? toCurrencyString(rebateEligibility?.amount, true)
              : '0',
          ],
        },
      ],
    },
    {
      sectionTitle: content.get('INSTALLATION_QUESTIONS'),
      items: [
        {
          label: content.get('HOW_SOON_FOR_INSTALLTION'),
          values: [sectionEvInstallInfo?.schedulePreference?.urgency || 'NA'],
        },
        {
          label: content.get('PREFERRED_INSTALLATION_DAY_TIME'),
          values: [
            sectionEvInstallInfo?.schedulePreference?.dayOfWeek
              ?.map(day => titleCase(day))
              .join(', ') || '',
            sectionEvInstallInfo?.schedulePreference?.preferredServiceTime?.join(', ') || ''
          ],
        },
        {
          label: content.get('REUSABLE_MAIN_BREAKER_PANEL_LOCATION'),
          values: [
            (sectionEvInstallInfo?.panelLocation?.location
              ? PANEL_LOCATION[sectionEvInstallInfo.panelLocation.location]
              : 'NA') as string,
          ],
        },
        {
          label: content.get('DISTANCE_PANEL_TO_CHARGER'),
          values: [
            (sectionEvInstallInfo?.chargerInstallPreference?.distanceToPanel
              ? DISTANCE_TO_PANEL[
                  sectionEvInstallInfo.chargerInstallPreference.distanceToPanel
                ]
              : 'NA') as string,
          ],
        },
        {
          label: content.get('PANEL_LOCATION_TEXT'),
          values: [
            (sectionEvInstallInfo?.chargerInstallPreference?.panelSideLocation
              ? PANEL_SIDE_LOCATION[
                  sectionEvInstallInfo.chargerInstallPreference
                    .panelSideLocation
                ]
              : 'NA') as string,
          ],
        },
      ],
    },
    {
      sectionTitle: content.get('UPLOADED_PHOTOS'),
      linkText: isLinkDownload
        ? content.get('DOWNLOAD_ALL_LINK')
        : content.get('VIEW_ALL'),
      onLinkClick: (e: React.MouseEvent<Element, MouseEvent>) =>
        isLinkDownload
          ? handleDownloadWithImage(e, uploadedPhotos)
          : onViewAll(),
      isDownloading,
      items: [
        {
          label: content.get('AMPERAGE_ON_MAIN_BREAKER'),
          values: [
            (sectionEvPanelInfo?.breakerInfo?.amperageRating
              ? MAIN_BREAKER_AMPS[
                  sectionEvPanelInfo.breakerInfo.amperageRating
                ]
              : 'NA') as string,
          ],
        },
      ],
    },
  ];

  return (
    <Fragment>
      <Box>
        <Paper
          css={{ padding: '25px ', backgroundColor: colors.white }}
          id={'print_section'}
        >
          {showCustomerInfoSection && (
            <>
              <Grid container>
                <Grid item xs={12}>
                  <Typography
                    css={{
                      fontSize: '16px !important',
                      fontWeight: 700,
                      color: colors.noirBlur,
                      marginBottom: '16px',
                    }}
                  >
                    {content.get('CUSTOMER_DETAILS')}
                  </Typography>
                </Grid>
                <Grid item container css={{ paddingLeft: '18px' }}>
                  <Grid item xs={3}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {content.get('NAME_LABEL')}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {sectionEvAccountInfo.accountContact.firstName}{' '}
                      {sectionEvAccountInfo.accountContact.lastName}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid item container css={{ paddingLeft: '18px' }}>
                  <Grid item xs={3}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {content.get('SERVICE_ADDRESS_LABEL')}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {sectionEvAccountInfo.address?.addressLine1}{' '}
                      {sectionEvAccountInfo.address?.city}
                      {', '}
                      {sectionEvAccountInfo.address?.state}{' '}
                      {sectionEvAccountInfo.address?.postal}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid item container css={{ paddingLeft: '18px' }}>
                  <Grid item xs={3}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {content.get('PHONE_LABEL')}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {sectionEvAccountInfo.accountContact.phoneNumber}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid item container css={{ paddingLeft: '18px' }}>
                  <Grid item xs={3}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {content.get('EMAIL_ADDRESS_LABEL')}
                    </Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography
                      variant="body2"
                      css={{
                        fontSize: '16px !important',
                        color: colors.noirBlur,
                        marginBottom: '16px',
                      }}
                    >
                      {sectionEvAccountInfo.accountContact.emailAddress}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Divider css={{ color: colors.gray93, marginBottom: '16px' }} />
              {sectionEvAccountInfo?.preferredContact && (
                <>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography
                        css={{
                          fontSize: '16px !important',
                          fontWeight: 700,
                          color: colors.noirBlur,
                          marginBottom: '16px',
                        }}
                      >
                        {content.get('CONTACT_DETAILS')}
                      </Typography>
                    </Grid>
                    <Grid item container css={{ paddingLeft: '18px' }}>
                      <Grid item xs={3}>
                        <Typography
                          variant="body2"
                          css={{
                            fontSize: '16px !important',
                            color: colors.noirBlur,
                            marginBottom: '16px',
                          }}
                        >
                          {content.get('MAIN_CONTACT_LABEL')}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="body2"
                          css={{
                            fontSize: '16px !important',
                            color: colors.noirBlur,
                            marginBottom: '16px',
                          }}
                        >
                          {sectionEvAccountInfo.preferredContact?.firstName}{' '}
                          {sectionEvAccountInfo.preferredContact?.lastName}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid item container css={{ paddingLeft: '18px' }}>
                      <Grid item xs={3}>
                        <Typography
                          variant="body2"
                          css={{
                            fontSize: '16px !important',
                            color: colors.noirBlur,
                            marginBottom: '16px',
                          }}
                        >
                          {content.get('PHONE_LABEL')}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="body2"
                          css={{
                            fontSize: '16px !important',
                            color: colors.noirBlur,
                            marginBottom: '16px',
                          }}
                        >
                          {sectionEvAccountInfo.preferredContact?.phoneNumber}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid item container css={{ paddingLeft: '18px' }}>
                      <Grid item xs={3}>
                        <Typography
                          variant="body2"
                          css={{
                            fontSize: '16px !important',
                            color: colors.noirBlur,
                            marginBottom: '16px',
                          }}
                        >
                          {content.get('EMAIL_ADDRESS_LABEL')}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="body2"
                          css={{
                            fontSize: '16px !important',
                            color: colors.noirBlur,
                            marginBottom: '16px',
                          }}
                        >
                          {sectionEvAccountInfo.preferredContact?.emailAddress}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Divider
                    css={{ color: colors.gray93, marginBottom: '16px' }}
                  />
                </>
              )}
            </>
          )}

          {summaryData.map((summaryItem, idx) => (
            <>
              <JobSummarySection {...summaryItem} />

              {idx !== summaryData.length - 1 && (
                <Divider css={{ color: colors.gray93, marginBottom: '16px' }} />
              )}
            </>
          ))}

          {uploadedPhotos && uploadedPhotos.length > 0 && (
            <ImageCarouselModal
              onClose={() => setIsImageViewerVisible(false)}
              open={isImageViewerVisible}
              content={content}
              photos={uploadedPhotos}
            />
          )}
        </Paper>
      </Box>
    </Fragment>
  );
};
